import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class13GuidedPractice01 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')} 
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>
      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4">The Accidental Rainforest</h2>

              {/* First paragraph - centered, italic */}
              <p className="text-[14px] italic font-semibold mb-4 text-center">
                According to ecological theory, rainforests are supposed to develop slowly over millions of years. But now ecologists are being forced to reconsider their ideas
              </p>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  When Peter Osbeck, a Swedish priest, stopped off at the mid-Atlantic Island of Ascension in 1752 on his way home from China, he wrote of ‘a heap of ruinous rocks’ with a bare, white mountain in the middle. All it boasted was a couple of dozen species of plant, most of them ferns and some of them unique to the island.
                </p>
                <p>
                  And so it might have remained. But in 1843 British plant collector Joseph Hooker made a brief call on his return from Antarctica. Surveying the bare earth, he concluded that the island had suffered some natural calamity that had denuded it of vegetation and triggered a decline in rainfall that was turning the place into a desert. The British Navy, which by then maintained a garrison on the island, was keen to improve the place and asked Hooker's advice. He suggested an ambitious scheme for planting trees and shrubs that would revive rainfall and stimulate a wider ecological recovery. And, perhaps lacking anything else to do, the sailors set to with a will.
                </p>
                <p>
                  In 1845, a naval transport ship from Argentina delivered a batch of seedlings. In the following years, more than 200 species of plant arrived from South Africa, from England came 700 packets of seeds, including those of two species that especially liked the place: bamboo and prickly pear. With sailors planting several thousand trees a year, the bare white mountain was soon cloaked in green and renamed Green Mountain, and by the early twentieth century the mountain's slopes were covered with a variety of trees and shrubs from all over the world.
                </p>
                <p>
                  Modern ecologists throw up their hands in horror at what they see as Hookers environmental anarchy. The exotic species wrecked the indigenous ecosystem, squeezing out the islands endemic plants. In fact. Hooker knew well enough what might happen. However, he saw greater benefit in improving rainfall and encouraging more prolific vegetation on the island.
                </p>
                <p>
                  But there is a much deeper issue here than the relative benefits of sparse endemic species versus luxuriant imported ones. And as botanist David Wilkinson of Liverpool John Moores University in the UK pointed out after a recent visit to the island, it goes to the heart of some of the most dearly held tenets of ecology. Conservationists' understandable concern for the fate of Ascension’s handful of unique species has, he says, blinded them to something quite astonishing the fact that the introduced species have been a roaring success.
                </p>
                <p>
                  Today's Green Mountain, says Wilkinson, is ‘a fully functioning man-made tropical cloud forest' that has grown from scratch from a ragbag of species collected more or less at random from all over the planet. But how could it have happened? Conventional ecological theory says that complex ecosystems such as cloud forests can emerge only through evolutionary processes in which each organism develops in concert with others to fill particular niches. Plants eo-evolve with their pollinators and seed dispersers, while microbes in the soil evolve to deal with the leaf litter.
                </p>
                <p>
                  But that’s not what happened on Green Mountain. And the experience suggests that perhaps natural rainforests are constructed far more by chance than by evolution. Species, say some ecologists, don’t so much evolve to create ecosystems as make the best of what they have. ‘The Green Mountain system is a man-made system that has produced a tropical rainforest without any co-evolution between its constituent species,’ says Wilkinson.
                </p>
                <p>
                  Not everyone agrees. Alan Gray, an ecologist at the University of Edinburgh in the UK. argues that the surviving endemic species on Green Mountain, though small in number, may still form the framework of the new' ecosystem. The new arrivals may just be an adornment, with little structural importance for the ecosystem.
                </p>
                <p>
                  But to Wilkinson this sounds like clutching at straws. And the idea of the instant formation of rainforests sounds increasingly plausible as research reveals that supposedly pristine tropical rainforests from the Amazon to south-east Asia may in places be little more titan the overgrown gardens of past rainforest civilisations.
                </p>
                <p>
                  The most surprising thing of all is that no ecologists have thought to conduct proper research into this human-made rainforest ecosystem. A survey of the island’s flora conducted six years ago by the University of Edinburgh was concerned only with endemic species. They characterised everything else as a threat. And the Ascension authorities are currently turning Green Mountain into a national park where introduced species, at least the invasive ones, are earmarked for culling rather than conservation.
                </p>
                <p>
                  Conservationists have understandable concerns, Wilkinson says. At least four endemic species have gone extinct on Ascension since the exotics started arriving. But in their urgency to protect endemics, ecologists are missing out on the study of a great enigma.
                </p>
                <p>
                  ‘As you walk through the forest, you see lots of leaves that have had chunks taken out of them by various insects. There are caterpillars and beetles around.' says Wilkinson. ‘But where did they come from? Are they endemic or alien? If alien, did they come with the plant on which they feed or discover it on arrival?’ Such questions go to the heart of how- rainforests happen.
                </p>
                <p>
                  The Green Mountain Forest holds many secrets. And the irony is that the most artificial rainforest in the world could tell us more about rainforest ecology than any number of natural forests.
                </p>
              </div>
            </div>
          </div>



          {/* Right: Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
          >
            {/* Questions 27-32 */}
            <div className="mb-10 w-full">
              <h3 className="text-[15px] font-bold mb-4 text-left">Questions 27 – 32</h3>
              <p className="text-[14px] mb-6 text-left">
                Choose <strong>TRUE</strong> if the statement agrees with the information given in the text, choose <strong>FALSE</strong> if the statement contradicts the information, or choose <strong>NOT GIVEN</strong> if there is no information on this.
              </p>

              <div className="space-y-2">
                {[
                  "When Peter Osbeck visited Ascension, he found no inhabitants on the island.",
                  "The natural vegetation on the island contained some species which were found nowhere else.",
                  "Joseph Hooker assumed that human activity had caused the decline in the island's plant life.",
                  "British sailors on the island took part in a major tree planting project.",
                  "Hooker sent details of his planting scheme to a number of different countries.",
                  "The bamboo and prickly pear seeds sent from England were unsuitable for Ascension."
                ].map((questionText, index) => {
                  const questionNum = 27 + index;
                  const isOpen = currentQuestion === questionNum;

                  return (
                    <div
                      key={questionNum}
                      className="mb-2 rounded bg-[#f5f8fc]"
                    >
                      {/* Question Header */}
                      <div
                        className="px-3 py-1.5 cursor-pointer"
                        onClick={() => setCurrentQuestion(isOpen ? null : questionNum)}
                      >
                        <div className="flex items-start gap-x-3">
                          <span className="font-semibold text-[14px]">{questionNum}</span>
                          <span className="text-[14px]">{questionText}</span>
                        </div>
                      </div>

                      {/* Answer Options */}
                      {isOpen && (
                        <div className="mt-1 mb-2 px-4 py-2 pl-6 flex flex-col gap-2 bg-[#D7DFEC] rounded-b-md">
                          {['TRUE', 'FALSE', 'NOT GIVEN'].map((opt) => (
                            <label
                              key={opt}
                              className="flex items-center text-[13px] cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={`tfnq${questionNum}`}
                                value={opt}
                                checked={answers[questionNum - 1] === opt}
                                onChange={() => {
                                  const newAnswers = [...answers];
                                  newAnswers[questionNum - 1] = opt;
                                  setAnswers(newAnswers);
                                }}
                                className="mr-2 accent-blue-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>







        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-2xl">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>
          {/* Numbers with lines above */}
          <div className="flex items-end space-x-2">
            {[33, 34, 35, 36].map((num) => {
              const isActive = currentQuestion === num;
              const isAnswered = answers[num - 1] && answers[num - 1] !== '';
              return (
                <div key={num} className="flex flex-col items-center">
                  {/* Line above number */}
                  <div
                    className="h-1 w-7 mb-1 rounded-full"
                    style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                  ></div>

                  {/* Number button */}
                  <button
                    onClick={() => setCurrentQuestion(num)}
                    className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
                      ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
                      transition-colors duration-150`}
                    style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                  >
                    {num}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>







    </div>
  );
};

export default Class13GuidedPractice01;
